import { useMemo, useState, type ReactNode } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Info, AlertTriangle, ExternalLink, ChevronRight } from 'lucide-react'
import { useIngresosPorMesCuenta, useMovimientosGasto } from '@/hooks/useReporteFinanciero'
import { useGastosMensuales } from '@/hooks/useGastos'
import { calcularPeriodo, PRESET_DEFAULT, type Periodo } from '@/lib/periodo-reporte'
import { mensajeErrorDeCarga } from '@/lib/permisos-guard'
import { cn, formatCurrency, formatDate } from '@/lib/utils'
import { GRUPO_RESULTADO_LABEL, type GrupoResultado } from '@/types/estadoResultado'

type CanalCobro = 'efectivo' | 'tarjeta' | 'transferencia' | 'otros'

/** Canal de cobro según el tipo de cuenta del pago. Las claves van en minúscula, como las devuelve la RPC. */
const CANAL_POR_TIPO_CUENTA: Record<string, CanalCobro> = {
  efectivo: 'efectivo',
  caja: 'efectivo',
  tarjeta: 'tarjeta',
  banco: 'transferencia',
  billetera: 'transferencia',
  wallet: 'transferencia',
}

const CANAL_LABEL: Record<CanalCobro, string> = {
  efectivo: 'Efectivo',
  tarjeta: 'Tarjeta',
  transferencia: 'Transferencia',
  otros: 'Otros',
}

/** Período de meses completos que cubre `periodo`: del día 1 de su primer mes al último día de su último mes. */
const mesesCompletos = (periodo: Periodo): Periodo => {
  const [anio, mes] = periodo.hasta.split('-').map(Number)
  const ultimoDia = String(new Date(anio, mes, 0).getDate()).padStart(2, '0')
  return { desde: `${periodo.desde.slice(0, 7)}-01`, hasta: `${periodo.hasta.slice(0, 7)}-${ultimoDia}` }
}

const formatPct = (valor: number, base: number): string =>
  base > 0 ? `${((valor / base) * 100).toFixed(1)}%` : '-'

interface ItemGasto {
  categoriaId: string | null
  categoria: string
  total: number
}
interface Grupo {
  total: number
  items: ItemGasto[]
}
const grupoVacio = (): Grupo => ({ total: 0, items: [] })

interface EstadoResultado {
  ingresosPorCanal: { canal: CanalCobro; total: number }[]
  totalIngresos: number
  grupos: Record<GrupoResultado, Grupo>
  sinClasificar: Grupo
  utilidadBruta: number
  ebit: number
  resultadoNeto: number
  totalGastos: number
  totalRetiros: number
}

/**
 * Estado de Resultado del estudio en base caja, solo ARS.
 * Cascada: Ingresos por canal − Operativos = Utilidad Bruta;
 * − Comerciales − Administrativos = Resultado Operativo (EBIT); − Financieros = Resultado Neto.
 * Cada gasto se imputa al grupo de su categoría. Los ingresos cuentan los pagos con fecha en el período.
 * Los gastos cuentan completos los meses que toca el período, porque la vista los agrupa por mes.
 * Cada grupo se despliega en categorías, y cada categoría en sus movimientos, que se piden al abrirla.
 * Sin `periodo`, muestra el último año.
 */
export function ReporteEstadoResultado({ periodo }: { periodo?: Periodo }) {
  const actual = useMemo(() => periodo ?? calcularPeriodo(PRESET_DEFAULT, new Date()).actual, [periodo])
  const detalle = useMemo(() => mesesCompletos(actual), [actual])

  const { data: ingresos = [], isLoading: loadingIng, error: errorIng } = useIngresosPorMesCuenta(actual)
  const {
    data: gastosRows = [],
    isLoading: loadingGas,
    error: errorGas,
  } = useGastosMensuales(detalle.desde.slice(0, 7))

  const gastosDelPeriodo = useMemo(
    () => gastosRows.filter((r) => r.mes_anio <= detalle.hasta.slice(0, 7)),
    [gastosRows, detalle],
  )
  // Solo ARS para la cascada. Aviso si hay otras monedas.
  const hayOtrasMonedas = gastosDelPeriodo.some((r) => r.moneda && r.moneda !== 'ARS')
  const gastosArs = useMemo(
    () => gastosDelPeriodo.filter((r) => !r.moneda || r.moneda === 'ARS'),
    [gastosDelPeriodo],
  )

  const estado = useMemo<EstadoResultado>(() => {
    const canalMap = new Map<CanalCobro, number>()
    for (const i of ingresos) {
      const canal = CANAL_POR_TIPO_CUENTA[i.tipoCuenta] ?? 'otros'
      canalMap.set(canal, (canalMap.get(canal) ?? 0) + i.total)
    }
    const ingresosPorCanal = (['efectivo', 'tarjeta', 'transferencia', 'otros'] as CanalCobro[])
      .map((canal) => ({ canal, total: canalMap.get(canal) ?? 0 }))
      .filter((c) => c.total !== 0)
    const totalIngresos = ingresosPorCanal.reduce((acc, c) => acc + c.total, 0)

    // Gastos por grupo (solo categoria_tipo 'gasto'); retiros aparte; sin grupo → sinClasificar.
    const grupos: Record<GrupoResultado, Grupo> = {
      operativo: grupoVacio(),
      comercial: grupoVacio(),
      administrativo: grupoVacio(),
      financiero: grupoVacio(),
    }
    const sinClasificar = grupoVacio()
    let totalRetiros = 0

    for (const g of gastosArs) {
      if (g.categoria_tipo === 'retiro') {
        totalRetiros += g.total
        continue
      }
      if (g.categoria_tipo !== 'gasto') continue
      const destino =
        g.grupo_resultado && g.grupo_resultado in grupos
          ? grupos[g.grupo_resultado as GrupoResultado]
          : sinClasificar
      destino.total += g.total
      const existente = destino.items.find((it) => it.categoriaId === g.categoria_id)
      if (existente) existente.total += g.total
      else destino.items.push({ categoriaId: g.categoria_id, categoria: g.categoria, total: g.total })
    }
    for (const grp of [...Object.values(grupos), sinClasificar]) {
      grp.items.sort((a, b) => b.total - a.total)
    }

    const utilidadBruta = totalIngresos - grupos.operativo.total
    const ebit = utilidadBruta - grupos.comercial.total - grupos.administrativo.total
    const resultadoNeto = ebit - grupos.financiero.total - sinClasificar.total
    const totalGastos =
      grupos.operativo.total +
      grupos.comercial.total +
      grupos.administrativo.total +
      grupos.financiero.total +
      sinClasificar.total

    return {
      ingresosPorCanal,
      totalIngresos,
      grupos,
      sinClasificar,
      utilidadBruta,
      ebit,
      resultadoNeto,
      totalGastos,
      totalRetiros,
    }
  }, [gastosArs, ingresos])

  const loading = loadingIng || loadingGas
  // Sin ingresos o sin gastos la cascada daría un resultado falso, así que un error de cualquiera lo reemplaza.
  const errorCarga = errorIng ?? errorGas
  const ti = estado.totalIngresos

  // Punto de equilibrio (fórmula del Excel): (com + adm + fin) / (1 − op/ingresos).
  const puntoEquilibrio = useMemo(() => {
    const { operativo, comercial, administrativo, financiero } = estado.grupos
    const margenContribucion = ti > 0 ? 1 - operativo.total / ti : 0
    if (margenContribucion <= 0) return null
    return (comercial.total + administrativo.total + financiero.total) / margenContribucion
  }, [estado, ti])

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/40 border rounded-md px-3 py-2">
        <Info className="h-4 w-4 shrink-0 mt-0.5" />
        <span>
          <strong>Estado de Resultado</strong> en <strong>base caja</strong> (solo ARS). Ingresos por
          canal de cobro; gastos imputados al grupo de su categoría. Los retiros de socios se informan
          aparte (no afectan el resultado).
          {hayOtrasMonedas && ' Hay gastos en otra moneda: se excluyen de este reporte.'}
        </span>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Estado de Resultado</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="h-32 flex items-center justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
            </div>
          ) : errorCarga ? (
            <p className="text-center text-destructive py-6">{mensajeErrorDeCarga(errorCarga)}</p>
          ) : estado.totalIngresos === 0 && estado.totalGastos === 0 ? (
            <p className="text-center text-muted-foreground py-6">Sin datos en el período seleccionado.</p>
          ) : (
            <div className="rounded-md border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-2 font-medium">Concepto</th>
                    <th className="text-right p-2 font-medium">Importe</th>
                    <th className="text-right p-2 font-medium w-24">% s/ ing.</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Ingresos */}
                  <SeccionHeader label="Ingresos por ventas" />
                  {estado.ingresosPorCanal.map((c) => (
                    <FilaItem key={c.canal} label={CANAL_LABEL[c.canal]} valor={c.total} base={ti} />
                  ))}
                  <FilaTotal label="Total ingresos" valor={ti} base={ti} positivo />

                  {/* Operativos → Utilidad bruta */}
                  <SeccionGrupo grupo="operativo" data={estado.grupos.operativo} base={ti} periodo={detalle} />
                  <FilaResultado label="= Utilidad bruta" valor={estado.utilidadBruta} base={ti} />

                  {/* Comerciales + Administrativos → EBIT */}
                  <SeccionGrupo grupo="comercial" data={estado.grupos.comercial} base={ti} periodo={detalle} />
                  <SeccionGrupo grupo="administrativo" data={estado.grupos.administrativo} base={ti} periodo={detalle} />
                  <FilaResultado label="= Resultado operativo (EBIT)" valor={estado.ebit} base={ti} />

                  {/* Financieros → Resultado neto */}
                  <SeccionGrupo grupo="financiero" data={estado.grupos.financiero} base={ti} periodo={detalle} />

                  {/* Sin clasificar (resta del neto, marcado) */}
                  {estado.sinClasificar.total !== 0 && (
                    <SeccionGrupo data={estado.sinClasificar} base={ti} periodo={detalle} />
                  )}

                  <FilaResultado label="= Resultado neto" valor={estado.resultadoNeto} base={ti} fuerte />
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Indicadores */}
      {!loading && !errorCarga && (estado.totalIngresos > 0 || estado.totalGastos > 0) && (
        <div className="grid grid-cols-2 gap-3">
          <Indicador label="Total gastos" valor={formatCurrency(estado.totalGastos)} />
          <Indicador label="Margen bruto" valor={formatPct(estado.utilidadBruta, ti)} />
          <Indicador label="Rentabilidad neta" valor={formatPct(estado.resultadoNeto, ti)} />
          <Indicador
            label="Punto de equilibrio"
            valor={puntoEquilibrio === null ? '-' : formatCurrency(puntoEquilibrio)}
          />
        </div>
      )}

      {!errorCarga && estado.sinClasificar.total !== 0 && (
        <p className="text-xs text-amber-700 dark:text-amber-400 flex items-center gap-1 flex-wrap">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> Hay gastos sin clasificar. Asigná el grupo
          de esas categorías en "Gestionar categorías", o{' '}
          {/* ORR-182: enlace directo a Gastos (pestaña nueva) para revisar los movimientos sin
              clasificar sin tener que buscarlos a mano. */}
          <a
            href="/gastos"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 font-medium underline underline-offset-2 hover:text-amber-800 dark:hover:text-amber-300"
          >
            abrí Gastos
            <ExternalLink className="h-3 w-3" />
          </a>{' '}
          para revisarlos.
        </p>
      )}

      {estado.totalRetiros > 0 && (
        <p className="text-xs text-muted-foreground">
          Retiros de socios en el período (no incluidos en el resultado):{' '}
          {formatCurrency(estado.totalRetiros)}.
        </p>
      )}
    </div>
  )
}

function SeccionHeader({ label }: { label: string }) {
  return (
    <tr className="bg-muted/30">
      <td colSpan={3} className="p-2 font-semibold text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </td>
    </tr>
  )
}

/** Botón que despliega una fila, con la flecha que indica si está abierta. */
function BotonDesplegar({ abierto, onClick, children }: { abierto: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" aria-expanded={abierto} onClick={onClick} className="flex items-center gap-1 text-left">
      <ChevronRight className={cn('h-3.5 w-3.5 shrink-0 transition-transform', abierto && 'rotate-90')} />
      {children}
    </button>
  )
}

/**
 * Fila plegable de un grupo de gastos: el total del grupo, y sus categorías al desplegarla.
 * Sin `grupo`, es la fila de los gastos sin clasificar.
 */
function SeccionGrupo({ grupo, data, base, periodo }: { grupo?: GrupoResultado; data: Grupo; base: number; periodo: Periodo }) {
  const [abierto, setAbierto] = useState(false)
  const alerta = !grupo ? 'text-amber-700 dark:text-amber-400' : ''
  return (
    <>
      <tr className={cn('border-t', grupo ? 'bg-muted/20' : 'bg-amber-50 dark:bg-amber-950/30')}>
        <td className={cn('p-2 font-medium', alerta)}>
          <BotonDesplegar abierto={abierto} onClick={() => setAbierto(!abierto)}>
            {grupo ? `Gastos ${GRUPO_RESULTADO_LABEL[grupo].toLowerCase()}` : 'Sin clasificar'}
            {!grupo && <AlertTriangle className="h-3.5 w-3.5" />}
          </BotonDesplegar>
        </td>
        <td className={cn('p-2 text-right tabular-nums font-medium', alerta)}>{formatCurrency(data.total)}</td>
        <td className="p-2 text-right tabular-nums text-muted-foreground">{formatPct(data.total, base)}</td>
      </tr>
      {abierto &&
        data.items.map((it) => <FilaCategoria key={it.categoriaId ?? 'sin'} item={it} base={base} periodo={periodo} />)}
    </>
  )
}

/** Fila plegable de una categoría. Al desplegarla pide sus movimientos del período. */
function FilaCategoria({ item, base, periodo }: { item: ItemGasto; base: number; periodo: Periodo }) {
  const [abierto, setAbierto] = useState(false)
  const { data: movimientos = [], isLoading, isError } = useMovimientosGasto(item.categoriaId, periodo, abierto)
  const aviso = isLoading
    ? 'Cargando movimientos…'
    : isError
      ? 'No se pudieron cargar los movimientos.'
      : movimientos.length === 0
        ? 'Sin movimientos en el período.'
        : null

  return (
    <>
      <tr className="border-t">
        <td className="p-2 pl-6 text-muted-foreground">
          <BotonDesplegar abierto={abierto} onClick={() => setAbierto(!abierto)}>
            {item.categoria}
          </BotonDesplegar>
        </td>
        <td className="p-2 text-right tabular-nums">{formatCurrency(item.total)}</td>
        <td className="p-2 text-right tabular-nums text-muted-foreground">{formatPct(item.total, base)}</td>
      </tr>
      {abierto && aviso && (
        <tr className="border-t text-xs text-muted-foreground">
          <td colSpan={3} className="p-2 pl-12">
            {aviso}
          </td>
        </tr>
      )}
      {abierto &&
        !aviso &&
        movimientos.map((m) => (
          <tr key={m.id} className="border-t text-xs" data-testid="movimiento-gasto">
            <td className="p-1.5 pl-12 text-muted-foreground">
              {formatDate(m.fecha)} · {m.descripcion}
            </td>
            <td className="p-1.5 text-right tabular-nums">{formatCurrency(m.monto)}</td>
            <td />
          </tr>
        ))}
    </>
  )
}

function FilaItem({ label, valor, base }: { label: string; valor: number; base: number }) {
  return (
    <tr className="border-t">
      <td className="p-2 pl-6 text-muted-foreground">{label}</td>
      <td className="p-2 text-right tabular-nums">{formatCurrency(valor)}</td>
      <td className="p-2 text-right tabular-nums text-muted-foreground">{formatPct(valor, base)}</td>
    </tr>
  )
}

function FilaTotal({
  label,
  valor,
  base,
  positivo,
}: {
  label: string
  valor: number
  base: number
  positivo?: boolean
}) {
  return (
    <tr className="border-t bg-muted/20">
      <td className="p-2 font-medium">{label}</td>
      <td className={`p-2 text-right tabular-nums font-medium ${positivo ? 'text-green-600' : ''}`}>
        {formatCurrency(valor)}
      </td>
      <td className="p-2 text-right tabular-nums text-muted-foreground">{formatPct(valor, base)}</td>
    </tr>
  )
}

function FilaResultado({
  label,
  valor,
  base,
  fuerte,
}: {
  label: string
  valor: number
  base: number
  fuerte?: boolean
}) {
  return (
    <tr className="border-t-2 border-foreground/20">
      <td className={`p-2 ${fuerte ? 'font-bold' : 'font-semibold'}`}>{label}</td>
      <td
        className={`p-2 text-right tabular-nums ${fuerte ? 'font-bold text-base' : 'font-semibold'} ${
          valor >= 0 ? 'text-emerald-600' : 'text-red-600'
        }`}
      >
        {formatCurrency(valor)}
      </td>
      <td className="p-2 text-right tabular-nums text-muted-foreground">{formatPct(valor, base)}</td>
    </tr>
  )
}

function Indicador({ label, valor }: { label: string; valor: string }) {
  return (
    <Card>
      <CardContent className="p-3">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-lg font-bold tabular-nums">{valor}</p>
      </CardContent>
    </Card>
  )
}
